import {IFirebaseTopicModel} from "../firebase-topic-model.interface";
import {IServiceFactoryExtended} from "../../../service-factory-extended.interface";
import {ITriple8Connection} from "../../triple8/triple8-connection.inteface";

import {MessagesTopicEvents} from "./messages-topic-events";
import {IMessagesTopicViewModel} from "./messages-topic.view-model.interface";
import {Check} from "@sparkware/app-utils";
import {OmgRawData} from "./raw-data/omg.raw-data";
import {
    ToastItemKind,
    MessageAnimationType,
    MessageDisplayMode,
    ToastRawJsonData,
    ToastRawDataItem
} from "./raw-data/toast.raw-data";
import {KickRawData} from "./raw-data/kick.raw-data";
import {RestrictionsUpdateRawData} from "./raw-data/restrictions-update.raw-data";
import {GeoPeriodicUpdateRawData} from "./raw-data/periodic-geo.raw-data";
import {BetRejectedRawData} from "./raw-data/bet-rejected.raw-data";
import {PromotionChangedRawData} from "./raw-data/promotion-changed.raw-data";
import {IntrusiveMessageRawData, IntrusiveMessageRawItemData} from "./raw-data/intrusive-message.raw-data";
import {GamblingSessionActionRawData, GamblingSessionActionRawItem} from "./raw-data/gambling-session-action.raw-data";
import {FirebaseMessageType} from "../firebase-common-consts";
import {PlatformMessageType} from "./platform-message-type.enum";
import {OpenWithinGameType} from "./publish-data/omg.event-data";
import {KickEventData} from "./publish-data/kick.event-data";
import {RawReceivedMessage} from "./raw-received-message-data";
import {FirebaseAnalyticsReporter} from "../../firebase-analytics-reporter";


export class MessagesTopicModel implements IFirebaseTopicModel, IMessagesTopicViewModel {
    constructor(protected readonly services: IServiceFactoryExtended,
                protected readonly getConnection: () => Promise<ITriple8Connection>) {
        this.analytics = new FirebaseAnalyticsReporter(services);
    }

    protected readonly analytics: FirebaseAnalyticsReporter;

    getTopicName(): string {
        return 'messages';
    }

    getTopicLabel(): string {
        return 'Messages';
    }

    readonly events = new MessagesTopicEvents();

    private _unsubscribeCallback: (() => void) | null = null;
    async subscribe(): Promise<void> {

        if(this._unsubscribeCallback) {
            this.services.logger.error('Already subscribed to messages topic');
            return;
        }

        const connection = await this.getConnection();
        this._unsubscribeCallback = await connection.subscribeAsync(
            this.getTopicName(),
            (messages: RawReceivedMessage[]) => messages.forEach(msg => this._handleMessage(msg)),
            {
                omgVersion: this.services.configuration.servicesConfigs.PlayerMessages.Firebase.Messages.OMG.OMGVersion,
                toasterVersion:this.services.configuration.servicesConfigs.PlayerMessages.Firebase.Messages.Toasters.toasterVersion
            });

    }

    async unsubscribe(): Promise<void> {
        if(this._unsubscribeCallback) {
            try {
                this._unsubscribeCallback();
            } finally {
                this._unsubscribeCallback = null;
            }
        }
    }

    private _handleMessage = (message: RawReceivedMessage) => {
        if(message.type !== FirebaseMessageType.ADDED) {
            return; // only messages with type = ADDED are implemented
        }
        const rawData = message.data;

        if(!rawData) {
            return;
        }

        this.services.logger.debug(`Firebase message received ${rawData.PlatformMessagesType}`, rawData);

        switch (rawData.PlatformMessagesType) {
            case PlatformMessageType.OMG:
                this._handleOMG(rawData);
                break;
            case PlatformMessageType.TOASTER:
                this._handleToast(rawData);
                break;
            case PlatformMessageType.KICK:
                this._handleKick(rawData);
                break;
            case PlatformMessageType.INIT_GEO:
                this._handleGeoPeriodicUpdate(rawData);
                break;
            case PlatformMessageType.UPDATE_RESTRICTION_V2:
                this._handleUpdateRestrictions(rawData);
                break;
            case PlatformMessageType.BET_REJECTED:
                this._handleBetRejected(rawData);
                break;
            case PlatformMessageType.PROMOTION_DISPLAY_IN_CLIENT:
                this._handlePromotionChanged(rawData);
                break;
            case PlatformMessageType.INTRUSIVE_MESSAGE:
                this._handleIntrusiveMessage(rawData);
                break;
            case PlatformMessageType.GAMBLING_SESSION_ACTION:
                this._handleGamblingSessionAction(rawData);
                break;
            default:
                this.services.logger.error('Unknown firebase message', rawData);
        }

        const analyticsLabel = PlatformMessageType[rawData.PlatformMessagesType];
        if(analyticsLabel) {
            this.analytics.sendMessageUpdate(analyticsLabel, JSON.stringify(rawData));
        }
    }

    private _handleOMG(rawData: OmgRawData): void {
        this.events.omg.notifyListeners({
            OmgType: rawData.OmgType.toString(),
            Messages: rawData.MessageData,
            OpenedWithinGame: (rawData.DisplayInGame?.toString() as OpenWithinGameType) ?? '0'
        });
    }

    private _handleToast(rawData: ToastRawJsonData): void {
        if(!rawData.MessageDataJson) {
            return;
        }

        let items: ToastItemKind | ToastItemKind[];

        try {
            items = JSON.parse(rawData.MessageDataJson);
        } catch (err) {
            this.services.logger.error(`Failed to parse toasts MessageDataJson: ${rawData.MessageDataJson}`, err);
            return;
        }

        if(!Check.isArray(items)) {
            items = [items]
        }

        const playerBurnoutMessages: ToastItemKind[] = [];
        const regularToasterMessages: ToastRawDataItem[] = [];

        items.forEach(item => {
            if(item.DisplayMode === MessageDisplayMode.ANIMATION &&
                item.AnimationType === MessageAnimationType.BURNOUT) {
                playerBurnoutMessages.push(item);
            } else {
                regularToasterMessages.push(item as ToastRawDataItem);
            }
        });

        //This comes as an array from Firebase, but in the burnout scenario
        // (where we can show only one animation at a given time),
        // I think it's correct to only consider the 1st one in the array
        if(playerBurnoutMessages.length > 0) {
            this.events.burnout.notifyListeners();
        }

        if(regularToasterMessages.length > 0) {
            this.events.toast.notifyListeners(regularToasterMessages)
        }

    }

    private _handleKick(rawData: KickRawData) {
        const eventData: KickEventData = {
            KickType: rawData.KickType.toString(),
            KickReasonId: rawData.KickReasonId.toString(),
            LimitExpirationDateInSeconds: rawData.LimitExpirationDate?.seconds,
            CorrelationID: rawData.CorrelationID || rawData.CorrelationId || ''
        }
        this.events.kick.notifyListeners(eventData);
        this.services.sdk.eventsTrigger.kick(eventData);
    }

    private _handleGeoPeriodicUpdate(rawData: GeoPeriodicUpdateRawData) {
        this.events.periodicGeo.notifyListeners({
            licenseKey: rawData.GeoComplyLicense,
            licenseWebKey: rawData.GeoComplyWebLicense,
            locationTransactionId: rawData.LocationTransactionId,
            mobileNumber: rawData.Mobile
        });
    }

    private _handleUpdateRestrictions(rawData: RestrictionsUpdateRawData) {
        this.events.restrictionsUpdate.notifyListeners(rawData.RestrictionsByAction);
        //geoRestrictionsUpdate must always be triggered after restrictionsUpdate
        this.events.geoRestrictionsUpdate.notifyListeners({
            KickReasonId: rawData.ReasonID?.toString(),
            DetailedMessages: rawData.TroubleshooterMessages,
            TroubleshooterRetry: Boolean(rawData.TroubleshooterRetry)
        });
    }

    private _handleBetRejected(rawData: BetRejectedRawData) {
        this.events.bonusAbuseParkingMoney.notifyListeners(rawData);
    }

    private _handlePromotionChanged(rawData: PromotionChangedRawData) {
        this.events.promotionChanged.notifyListeners(rawData);
    }

    private _handleIntrusiveMessage(rawData: IntrusiveMessageRawData) {
        if(!rawData.MessageDataJson) {
            return;
        }

        let items: IntrusiveMessageRawItemData | IntrusiveMessageRawItemData[];

        try {
            items = JSON.parse(rawData.MessageDataJson);
        } catch (err) {
            this.services.logger.error(`Failed to parse intrusive message MessageDataJson: ${rawData.MessageDataJson}`, err);
            return;
        }

        if(!Check.isArray(items)) {
            items = [items];
        }

        items.forEach(item => {
            this.services.orbitComponentInsideSdkProvider.showIntrusiveMessagePopup({
                title: item.Title,
                text: item.Text,
                buttonText: item.ButtonText,
                correlationId: item.CorrelationID
            });
        })

    }

    private _handleGamblingSessionAction(rawData: GamblingSessionActionRawData) {
        if(!rawData.MessageDataJson) {
            return;
        }

        let items: GamblingSessionActionRawItem | GamblingSessionActionRawItem[];
        try {
            items = JSON.parse(rawData.MessageDataJson);
        } catch (err) {
            this.services.logger.error(`Failed to parse gambling session event MessageDataJson: ${rawData.MessageDataJson}`, err);
            return;
        }

        if(!Check.isArray(items)) {
            items = [items];
        }

        items.forEach(item => {
            this.events.gamblingSessionAction.notifyListeners({
                messageType: item.MessageType,
                gamblingSessionId: item.SessionId,
                sessionDurationInSeconds: item.SessionDurationInSeconds,
                sessionTotalWins: item.SessionTotalWins,
                sessionTotalBets: item.SessionTotalBets,
                messageTimeStamp: item.MessageTimeStamp,
                correlationID: item.CorrelationID
            })
        });
    }
}